import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const PLATFORMS = {
  tradovate: {
    name: "Tradovate",
    logo: "T",
    color: "blue",
    description: "Cloud-based futures trading platform with REST & WebSocket API",
    features: ["Real-time market data", "Order execution", "Position management", "Account info"],
    fields: [
      { key: "username", label: "Username", type: "text", placeholder: "Your Tradovate username" },
      { key: "password", label: "Password", type: "password", placeholder: "Your Tradovate password" },
      { key: "app_id", label: "App ID", type: "text", placeholder: "Your API App ID" },
      { key: "app_version", label: "App Version", type: "text", placeholder: "1.0", defaultValue: "1.0" },
      { key: "cid", label: "Client ID", type: "text", placeholder: "Your Client ID" },
      { key: "secret", label: "Client Secret", type: "password", placeholder: "Your Client Secret" },
    ],
    docsUrl: "https://api.tradovate.com",
    setupSteps: [
      "Log into your Tradovate account",
      "Go to Settings → API Access",
      "Enable API access for your account",
      "Create a new API application to get credentials",
      "Copy your Client ID and Secret here",
    ],
  },
  ninjatrader: {
    name: "NinjaTrader",
    logo: "NT",
    color: "orange",
    description: "Professional trading platform with CrossTrade API bridge",
    features: ["Live/Paper trading", "Market data streaming", "Order management", "Strategy execution"],
    fields: [
      { key: "api_key", label: "CrossTrade API Key", type: "password", placeholder: "Your CrossTrade Bearer token" },
      { key: "account_id", label: "Account ID", type: "text", placeholder: "Your NinjaTrader account ID" },
      { key: "environment", label: "Environment", type: "select", options: ["Simulation", "Live"] },
    ],
    docsUrl: "https://crosstrade.io/crosstrade-api",
    setupSteps: [
      "Download and install NinjaTrader 8",
      "Install the CrossTrade add-on from crosstrade.io",
      "Create a CrossTrade account and get your API key",
      "Connect your NinjaTrader account to CrossTrade",
      "Copy your Bearer token and Account ID here",
    ],
  },
  rithmic: {
    name: "Rithmic",
    logo: "R",
    color: "green",
    description: "Low-latency futures trading via R | Protocol API",
    features: ["Sub-millisecond execution", "L2 order book", "Historical data", "Multi-account support"],
    fields: [
      { key: "username", label: "Rithmic Username", type: "text", placeholder: "Your Rithmic username" },
      { key: "password", label: "Rithmic Password", type: "password", placeholder: "Your Rithmic password" },
      { key: "server", label: "Server", type: "select", options: ["Rithmic Paper Trading", "Rithmic 01 (Chicago)", "Rithmic 04 (Europe)"] },
      { key: "gateway", label: "Gateway", type: "text", placeholder: "e.g., login.rithmic.com" },
    ],
    docsUrl: "https://www.rithmic.com/apis",
    setupSteps: [
      "Open a futures account with a Rithmic-compatible broker",
      "Request API access from your broker",
      "Get your Rithmic credentials",
      "Choose your server location for lowest latency",
      "Enter your credentials here to connect",
    ],
  },
};

export default function Connections() {
  const [connections, setConnections] = useState({});
  const [activeTab, setActiveTab] = useState("tradovate");
  const [formData, setFormData] = useState({});
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncedTrades, setSyncedTrades] = useState([]);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const res = await axios.get(`${API}/connections`);
      setConnections(res.data);
    } catch (err) {
      console.error("Failed to fetch connections:", err);
    }
  };

  const handleInputChange = (platform, field, value) => {
    setFormData(prev => ({
      ...prev,
      [platform]: {
        ...(prev[platform] || {}),
        [field]: value,
      },
    }));
  };

  const handleTestConnection = async (platform) => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/connections/${platform}/test`, formData[platform] || {});
      if (res.data.success) {
        toast.success(`${PLATFORMS[platform].name} connection successful!`);
      } else {
        toast.error(res.data.message || "Connection failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleSaveConnection = async (platform) => {
    setSaving(true);
    try {
      await axios.post(`${API}/connections/${platform}`, formData[platform] || {});
      toast.success(`${PLATFORMS[platform].name} credentials saved!`);
      fetchConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save connection");
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async (platform) => {
    if (!window.confirm(`Disconnect from ${PLATFORMS[platform].name}?`)) return;
    try {
      await axios.delete(`${API}/connections/${platform}`);
      toast.success(`Disconnected from ${PLATFORMS[platform].name}`);
      fetchConnections();
    } catch (err) {
      toast.error("Failed to disconnect");
    }
  };

  const handleSyncTrades = async (platform) => {
    setSyncing(true);
    setSelectedPlatform(platform);
    try {
      const res = await axios.post(`${API}/connections/${platform}/sync`);
      toast.success(res.data.message);
      
      // Fetch synced trades
      const tradesRes = await axios.get(`${API}/connections/${platform}/trades`);
      setSyncedTrades(tradesRes.data);
      setShowSyncModal(true);
      fetchConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleImportTrade = async (platform, tradeId) => {
    try {
      await axios.post(`${API}/connections/${platform}/import/${tradeId}`);
      toast.success("Trade imported to journal!");
      setSyncedTrades(prev => prev.filter(t => t.external_id !== tradeId));
    } catch (err) {
      toast.error("Failed to import trade");
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: "border-blue-500/30 bg-blue-500/10 text-blue-500",
      orange: "border-orange-500/30 bg-orange-500/10 text-orange-500",
      green: "border-emerald-500/30 bg-emerald-500/10 text-emerald-500",
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="p-6 space-y-6" data-testid="connections-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-mono font-bold text-white">TRADING CONNECTIONS</h1>
        <p className="text-sm text-slate-500 font-mono">Connect your trading platforms for seamless execution.</p>
      </div>

      {/* Platform Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#0A0A0A] border border-[#262626] p-1">
          {Object.entries(PLATFORMS).map(([key, platform]) => (
            <TabsTrigger
              key={key}
              value={key}
              data-testid={`tab-${key}`}
              className="font-mono text-sm data-[state=active]:bg-[#171717] data-[state=active]:text-white"
            >
              <span className={`w-6 h-6 mr-2 flex items-center justify-center text-xs font-bold border ${getColorClasses(platform.color)}`}>
                {platform.logo}
              </span>
              {platform.name}
              {connections[key]?.connected && (
                <span className="ml-2 w-2 h-2 rounded-full bg-emerald-500" />
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.entries(PLATFORMS).map(([key, platform]) => (
          <TabsContent key={key} value={key} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Connection Form */}
              <div className="lg:col-span-2 card-institutional p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-12 h-12 flex items-center justify-center text-lg font-bold border ${getColorClasses(platform.color)}`}>
                    {platform.logo}
                  </div>
                  <div>
                    <h2 className="font-mono text-lg text-white">{platform.name}</h2>
                    <p className="text-sm text-slate-500">{platform.description}</p>
                  </div>
                  {connections[key]?.connected && (
                    <span className="ml-auto px-3 py-1 text-xs font-mono bg-emerald-500/10 border border-emerald-500/30 text-emerald-500">
                      CONNECTED
                    </span>
                  )}
                </div>

                {/* Features */}
                <div className="grid grid-cols-2 gap-2 mb-6">
                  {platform.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                      <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </div>
                  ))}
                </div>

                {/* Form Fields */}
                <div className="space-y-4">
                  {platform.fields.map((field) => (
                    <div key={field.key}>
                      <label className="block text-xs font-mono text-slate-500 mb-2">{field.label.toUpperCase()}</label>
                      {field.type === "select" ? (
                        <select
                          value={formData[key]?.[field.key] || ""}
                          onChange={(e) => handleInputChange(key, field.key, e.target.value)}
                          data-testid={`${key}-${field.key}`}
                          className="w-full input-institutional px-4 py-3"
                        >
                          <option value="">Select...</option>
                          {field.options.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.type}
                          value={formData[key]?.[field.key] || field.defaultValue || ""}
                          onChange={(e) => handleInputChange(key, field.key, e.target.value)}
                          placeholder={field.placeholder}
                          data-testid={`${key}-${field.key}`}
                          className="w-full input-institutional px-4 py-3"
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 mt-6">
                  <button
                    onClick={() => handleTestConnection(key)}
                    disabled={testing}
                    data-testid={`test-${key}`}
                    className="btn-secondary flex-1"
                  >
                    {testing ? "TESTING..." : "TEST CONNECTION"}
                  </button>
                  <button
                    onClick={() => handleSaveConnection(key)}
                    disabled={saving}
                    data-testid={`save-${key}`}
                    className="btn-primary flex-1"
                  >
                    {saving ? "SAVING..." : "SAVE CREDENTIALS"}
                  </button>
                </div>

                {connections[key]?.connected && (
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => handleSyncTrades(key)}
                      disabled={syncing}
                      data-testid={`sync-${key}`}
                      className="flex-1 py-2 text-xs font-mono text-emerald-500 border border-emerald-500/30 hover:bg-emerald-500/10 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      {syncing ? "SYNCING..." : "SYNC TRADES"}
                    </button>
                    <button
                      onClick={() => handleDisconnect(key)}
                      className="flex-1 py-2 text-xs font-mono text-red-500 border border-red-500/30 hover:bg-red-500/10 transition-colors"
                    >
                      DISCONNECT
                    </button>
                  </div>
                )}
              </div>

              {/* Setup Guide */}
              <div className="card-institutional p-6">
                <h3 className="font-mono text-sm font-bold text-white mb-4">SETUP GUIDE</h3>
                <ol className="space-y-3">
                  {platform.setupSteps.map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-[#171717] border border-[#262626] flex items-center justify-center text-xs font-mono text-blue-500 flex-shrink-0">
                        {i + 1}
                      </span>
                      <p className="text-sm text-slate-400">{step}</p>
                    </li>
                  ))}
                </ol>

                <a
                  href={platform.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 mt-6 text-sm text-blue-500 hover:text-blue-400"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  View API Documentation
                </a>
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Status Overview */}
      <div className="card-institutional p-6">
        <h3 className="font-mono text-sm font-bold text-white mb-4">CONNECTION STATUS</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(PLATFORMS).map(([key, platform]) => (
            <div
              key={key}
              className={`p-4 border ${
                connections[key]?.connected
                  ? "border-emerald-500/30 bg-emerald-500/5"
                  : "border-[#262626]"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 flex items-center justify-center text-xs font-bold border ${getColorClasses(platform.color)}`}>
                    {platform.logo}
                  </span>
                  <span className="font-mono text-sm text-white">{platform.name}</span>
                </div>
                <span className={`text-xs font-mono ${
                  connections[key]?.connected ? "text-emerald-500" : "text-slate-500"
                }`}>
                  {connections[key]?.connected ? "ONLINE" : "OFFLINE"}
                </span>
              </div>
              {connections[key]?.last_sync && (
                <p className="text-xs text-slate-500 mt-2">
                  Last sync: {new Date(connections[key].last_sync).toLocaleString()}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/5 border border-blue-500/20 p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-mono text-sm text-blue-500 font-bold mb-1">SECURITY NOTE</p>
            <p className="text-sm text-slate-400">
              Your credentials are encrypted and stored securely. We never have access to your trading funds. 
              Connections are used only for data synchronization and optional trade execution through your authorized platforms.
            </p>
          </div>
        </div>
      </div>

      {/* Sync Trades Modal */}
      <Dialog open={showSyncModal} onOpenChange={setShowSyncModal}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-3xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-mono text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              SYNCED TRADES FROM {selectedPlatform?.toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4 max-h-[60vh] overflow-y-auto">
            {syncedTrades.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-12 h-12 mx-auto text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-slate-500 font-mono">No trades to sync</p>
                <p className="text-slate-600 text-sm mt-2">
                  When you have trades in your connected platform, they'll appear here for import.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {syncedTrades.map((trade) => (
                  <div
                    key={trade.external_id}
                    className="bg-[#050505] border border-[#262626] p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`px-2 py-1 text-xs font-mono ${
                        trade.direction === "LONG" 
                          ? "bg-emerald-500/20 text-emerald-500" 
                          : "bg-red-500/20 text-red-500"
                      }`}>
                        {trade.direction}
                      </div>
                      <div>
                        <p className="font-mono text-white font-bold">{trade.symbol}</p>
                        <p className="text-xs text-slate-500">
                          Entry: {trade.entry_price} | Size: {trade.position_size}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {trade.pnl !== null && (
                        <span className={`font-mono ${trade.pnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                          ${trade.pnl?.toFixed(2)}
                        </span>
                      )}
                      <span className={`text-xs font-mono px-2 py-1 ${
                        trade.status === "open" 
                          ? "bg-blue-500/20 text-blue-500" 
                          : "bg-slate-500/20 text-slate-400"
                      }`}>
                        {trade.status.toUpperCase()}
                      </span>
                      <button
                        onClick={() => handleImportTrade(selectedPlatform, trade.external_id)}
                        className="btn-primary text-xs px-4 py-2"
                      >
                        IMPORT
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="mt-4 pt-4 border-t border-[#262626] flex justify-between items-center">
            <p className="text-xs text-slate-500">
              {syncedTrades.length} trade(s) available for import
            </p>
            <button
              onClick={() => setShowSyncModal(false)}
              className="btn-secondary"
            >
              CLOSE
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
